"use client";
import Link from "next/link";
import Image from "next/image";
import React, {
  Fragment,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import SVG from "@/app/components/pages/SVG";

import { useRouter } from "@/i18n/navigation";
import { usePathname } from "next/navigation";
import { PAGES_LINKS } from "@/app/utils/constants/constants";
import useTracking from "@/app/common/utils/useTracking";
import ButtonSignIn from "./Loader/ButtonSignIn";

import { useWalletService } from "@/app/context/walletServiceContext/walletServiceContext";
import { CurrencyModal } from "./CurrencyModal";
import { CurrencyContext } from "@/app/context/Search/CurrencyContext";
import CurrencyImage from "./CurrencyModal/CurrencyImage";
import ThemeSwitch from "./ThemeSwitch";
import { useTheme } from "next-themes";
import { LightMoon, Moon } from "./Icons";
import { getPartnerCode } from "@/app/utils/services/service/localStorageService";
import { LangSwitcher } from "./LangSwitcher/LangSwitcher";
import { Globe, X } from "lucide-react";
import { useOnClickOutside } from "@/app/hooks/useOnClickOutside";
import { getFiatCurrencies } from "@/app/constants/Currencies";
import { useTranslations } from "next-intl";

function Header() {
  const tHeader = useTranslations("Header");
  const t = useTranslations();
  const { push } = useRouter();
  const [toggle, setToggle] = useState(false);
  const [showCurrencyModal, setShowCurrencyModal] = useState(false);
  const [selectedCurrencyIcon, setSelectedCurrencyIcon] = useState<ReactNode>();
  const { userDetails, loader } = useWalletService();
  const [languageDropdown, setLanguageDropdown] = useState(false);
  const pathname = usePathname();
  const isFlightsPage = pathname.includes("/flights");
  const [mounted, setMounted] = useState(false);
  const { setTheme, resolvedTheme } = useTheme();
  const [showLocale, setShowLocale] = useState(false);

  const languageDropdownRef = useOnClickOutside(() =>
    setLanguageDropdown(false)
  );

  useEffect(() => setMounted(true), []);

  const { walletId, points, isCBOneAttested } = userDetails;

  const { currencies, currency, setUpdateCurrency, rate } =
    useContext(CurrencyContext);

  useTracking();

  const handleSignIn = () => {
    if (walletId) {
      push("/account");
    }
  };

  useEffect(() => {
    if (!currencies.length) return;

    const savedCurrency = currencies.find(
      (item) => item.value === currency.value
    );

    if (savedCurrency) {
      if (savedCurrency.type === "FIAT") {
        setSelectedCurrencyIcon(
          getFiatCurrencies(t)?.find((c) => c?.label == savedCurrency?.value)
            ?.icon
        );
      } else {
        setSelectedCurrencyIcon(
          <CurrencyImage symbol={savedCurrency?.value} />
        );
      }
    } else {
      setSelectedCurrencyIcon(
        <Image
          src="/assets/v2/icons/flags/us.svg"
          height={32}
          alt="icon"
          width={32}
        />
      );
    }
  }, [currencies.length, rate]);

  const renderMenuIcon = (iconType: string) => (
    <>
      <SVG
        src={`/assets/v2/icons/${iconType}-dark.svg`}
        alt={`${iconType.replace("-", " ")} Icon`}
        twClasses="w-8 h-8 hidden dark:block"
      />
      <SVG
        src={`/assets/v2/icons/${iconType}.svg`}
        alt={`${iconType.replace("-", " ")} Icon`}
        twClasses="w-8 h-8 block dark:hidden"
      />
    </>
  );

  const partnerCode = getPartnerCode();
  const isCoinbasePartner = partnerCode === "coinbase";

  return (
    <div className="bg-lightwhite dark:bg-sleap-gray-950">
      <div className="container !pt-3 pb-2 flex mx-auto">
        <div className="flex items-center flex-auto">
          <div className="flex grow items-center navbar-start">
            <div
              onClick={() => setToggle(!toggle)}
              className="block relative lg:hidden pr-2"
            >
              {toggle
                ? renderMenuIcon("close-line")
                : renderMenuIcon("menu-line")}

              <div
                className={` ${
                  toggle ? "block" : "hidden"
                } absolute top-9 left-[0px] bg-lightwhite dark:bg-sleap-gray-850 w-[196px] border-2 border-gray-100 dark:border-sleap-gray-700 rounded-xl p-2 z-50`}
              >
                <ul className="dark:text-white text-sleap-gray-800 flex flex-col gap-2 justify-center mb-0 text-base font-medium">
                  <li onClick={() => setToggle(false)}>
                    {currencies.length ? (
                      <span
                        className="cursor-pointer flex dark:text-white text-sleap-gray-800 items-center gap-x-1 xs:px-4 px-3 py-2"
                        onClick={() => setShowCurrencyModal(true)}
                      >
                        {selectedCurrencyIcon} - {currency?.label}
                      </span>
                    ) : (
                      ""
                    )}
                  </li>
                  <li
                    className="lg:hidden block"
                    onClick={() => {
                      setShowLocale(true);
                      setToggle(false);
                    }}
                  >
                    <LangSwitcher
                      showLocale={showLocale}
                      setShowLocale={setShowLocale}
                    />{" "}
                  </li>
                  <li onClick={() => setToggle(false)}>
                    {!mounted && (
                      <span className="w-9 h-9 bg-transparent block"></span>
                    )}

                    {mounted && (
                      <span
                        onClick={() =>
                          setTheme(resolvedTheme === "dark" ? "light" : "dark")
                        }
                        className="cursor-pointer flex gap-3 px-3 py-2"
                      >
                        {resolvedTheme === "dark" ? <LightMoon /> : <Moon />}
                        <span>
                          {resolvedTheme === "dark"
                            ? tHeader("theme.light")
                            : tHeader("theme.dark")}{" "}
                        </span>
                      </span>
                    )}
                  </li>
                  <li>
                    <hr className="h-[1px] bg-gray-100 dark:bg-sleap-gray-700 border-0" />
                  </li>
                  <li onClick={() => setToggle(false)}>
                    <Link
                      className="dark:text-white text-sleap-gray-800 px-3 py-2"
                      href={PAGES_LINKS.POINTS}
                      target="_blank"
                    >
                      {tHeader("menu.points")}
                    </Link>
                  </li>
                  <li onClick={() => setToggle(false)}>
                    <Link
                      className="dark:text-white text-sleap-gray-800 px-3 py-2"
                      href={PAGES_LINKS.BENEFITS}
                      target="_blank"
                    >
                      {tHeader("menu.nftBenefits")}
                    </Link>
                  </li>
                  <li onClick={() => setToggle(false)}>
                    <Link
                      className="dark:text-white text-sleap-gray-800 px-3 py-2"
                      href={PAGES_LINKS.NFT}
                      target="_blank"
                    >
                      {tHeader("menu.nftCollection")}
                    </Link>
                  </li>
                  <li onClick={() => setToggle(false)}>
                    <Link
                      className="dark:text-white text-sleap-gray-800 px-3 py-2"
                      href={PAGES_LINKS.ABOUT}
                      target="_blank"
                    >
                      {tHeader("menu.about")}
                    </Link>
                  </li>
                  <li onClick={() => setToggle(false)}>
                    <Link
                      className="dark:text-white text-sleap-gray-800 px-3 py-2"
                      href={PAGES_LINKS.HELP}
                      target="_blank"
                    >
                      {tHeader("menu.help")}
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
            <div className="flex gap-1 md:gap-2">
              <a href={isFlightsPage ? "/flights" : "/"}>
                <Image
                  src="/assets/v2/sleap-staynex-white-logo.svg"
                  className="sm:h-16 h-14 sm:w-auto sm:max-w-[192px] max-w-[136px] dark:block hidden"
                  width={192}
                  height={60}
                  alt="Sleap Logo"
                />
                <Image
                  src="/assets/v2/sleap-staynex-dark-logo.svg"
                  className="sm:h-16 h-14 sm:w-auto sm:max-w-[192px] max-w-[136px] dark:hidden block"
                  width={192}
                  height={60}
                  alt="Sleap Logo"
                />
              </a>

              {isCoinbasePartner ? (
                <Fragment>
                  <div className="sm:h-10 h-6 w-[1px] bg-black dark:bg-white"></div>
                  <Image
                    src="/assets/v2/cb1-logo-dark.svg"
                    className="h-6 sm:w-auto sm:max-w-[156px] max-w-[106px] block dark:hidden"
                    width={156}
                    height={40}
                    alt="Partner Logo"
                  />
                  <Image
                    src="/assets/v2/cb1-logo-light.svg"
                    className="h-6 sm:w-auto sm:max-w-[156px] max-w-[106px] hidden dark:block"
                    width={156}
                    height={40}
                    alt="Partner Logo"
                  />
                </Fragment>
              ) : (
                ""
              )}
            </div>
            <div className="items-center gap-6 ml-10 hidden lg:flex"></div>
          </div>
          <div className="lg:flex justify-between gap-[18px] items-center hidden">
            <Link
              className=" font-normal text-base dark:text-white text-sleap-gray-800 hover:brightness-75"
              href={PAGES_LINKS.POINTS}
              target="_blank"
            >
              {tHeader("menu.points")}
            </Link>
            <Link
              className=" font-normal text-base dark:text-white text-sleap-gray-800 hover:brightness-75"
              href={PAGES_LINKS.BENEFITS}
              target="_blank"
            >
              {tHeader("menu.nftBenefits")}
            </Link>
            <Link
              className=" font-normal text-base dark:text-white text-sleap-gray-800 hover:brightness-75"
              href={PAGES_LINKS.NFT}
              target="_blank"
            >
              {tHeader("menu.nftCollection")}
            </Link>
            <Link
              className=" font-normal text-base dark:text-white text-sleap-gray-800 hover:brightness-75"
              href={PAGES_LINKS.ABOUT}
              target="_blank"
            >
              {tHeader("menu.about")}
            </Link>
            <Link
              className="font-normal text-base dark:text-white text-sleap-gray-800 hover:brightness-75"
              href={PAGES_LINKS.HELP}
              target="_blank"
            >
              {tHeader("menu.help")}
            </Link>
          </div>
          <div className="ml-2 p-2 hidden lg:block">
            <ThemeSwitch />
          </div>
          <div //@ts-ignore
            ref={languageDropdownRef}
            onClick={() => {
              setToggle(false);
              setLanguageDropdown(!languageDropdown);
            }}
            className="relative p-2 cursor-pointer hidden lg:block"
          >
            {languageDropdown ? (
              <X className="text-black dark:text-white cursor-pointer h-5 w-5" />
            ) : (
              <Globe className="h-5 w-5 text-black dark:text-white" />
            )}

            <div
              className={` ${
                languageDropdown ? "block" : "hidden"
              } absolute top-9 right-0 bg-white dark:bg-sleap-gray-950 w-[280px] border-2 border-sleap-off-white dark:border-sleap-gray-850 rounded-xl p-4 z-50`}
            >
              <ul className="dark:text-white text-sleap-gray-800 flex flex-col gap-2 justify-center mb-0 text-base font-medium">
                <li onClick={() => setLanguageDropdown(false)}>
                  <LangSwitcher />
                </li>
              </ul>
            </div>
          </div>{" "}
          {currencies.length ? (
            <span
              className="cursor-pointer hidden lg:flex dark:text-white text-sleap-gray-800 ml-2 items-center rounded-2xl border-[1px] 2xl:border-2  h-12 gap-x-1 xs:px-4 px-2 
          dark:border-input border-sleap-gray-800
          dark:hover:border-gray-200 "
              onClick={() => setShowCurrencyModal(true)}
            >
              {selectedCurrencyIcon} - {currency?.label}
            </span>
          ) : (
            ""
          )}
          <div className="flex items-center xs:gap-5 gap-3">
            <div className="flex items-center xs:gap-5 gap-3">
              <ButtonSignIn
                walletId={walletId}
                handleSignIn={handleSignIn}
                loader={loader}
                points={points}
                isCBOneAttested={isCBOneAttested}
              />
            </div>
          </div>
        </div>
        {showCurrencyModal && (
          <CurrencyModal
            setOpenModal={setShowCurrencyModal}
            openModal={showCurrencyModal}
            currency={currency}
            setUpdateCurrency={setUpdateCurrency}
            currencies={currencies}
          />
        )}
        <LangSwitcher
          showLocale={showLocale}
          setShowLocale={setShowLocale}
          isModal={true}
        />
      </div>
    </div>
  );
}

export default Header;
